/* eslint-disable max-lines */
/* eslint-disable max-lines-per-function */
import React from 'react'
import { tagAxes, tagAxesObject } from '@cpsy-rmap-project/tags'
import { FormControlLabel, Checkbox } from '@material-ui/core'
import Badge from '@material-ui/core/Badge'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import ButtonBase from '@material-ui/core/ButtonBase'
import { Theme, makeStyles, createStyles } from '@material-ui/core/styles'
import Typography from '@material-ui/core/Typography'
import _ from 'lodash'
import { useSpring, animated } from 'react-spring'
import styled from 'styled-components'
import { Colors, Layouts } from '../../../consts'
import { getFirstAuthor } from '../../../utils/paper'
import HelpPopover from '../../atoms/HelpPopover'
import SearchDisplayFilteringInfo from '../../atoms/SearchDisplayFilteringInfo'
import { useCprmSearch, HeatMapCellPapersData } from '../../pages/Search/hooks'
import { NeuroscienceHelpText, PsychiatryHelpText, ComputationHelpText } from './helpText'

export interface MapVector {
  x: number
  y: number
}

const useStyles = makeStyles((_theme: Theme) =>
  createStyles({
    mapCell: {
      color: '#555555',
      '&:hover, &$focusVisible': {
        color: '#000000'
      }
    },
    focusVisible: {},
    badge: {
      '& .MuiBadge-badge': {
        fontSize: '10px',
        minWidth: '16px',
        height: '16px',
        padding: '0px 2px',
        backgroundColor: '#666666dd',
        color: 'white'
      }
    }
  })
)

const XScrollableBox = styled(Box)`
  width: 100%;
  overflow: scroll;
  overflow-y: hidden;
  /* スクロールバー見た目変問題 */
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }
`

const Map = styled.div`
  width: 100%;
  padding: 0 0 0 30px;
`
const Row = styled.div`
  display: flex;
  flex-wrap: nowrap;
`

const CellText = styled.div`
  flex-basis: auto;
  margin: auto;
  text-align: center;
`

const CellReserchDefault = styled.div`
  width: 100%;
  padding: 6px;
  font-size: 0.9em;
  line-height: 1.1;
`

const CellDefault = styled.div`
  display: flex;
  min-width: ${Layouts.HeatMap.CellMinWidth};
  height: ${Layouts.HeatMap.CellHeight};
`

const CellInnerDefault = styled.div`
  display: flex;
  min-width: ${Layouts.HeatMap.CellMinWidth};
  height: ${Layouts.HeatMap.CellHeight};
`

interface CellReserchProps {
  reserchNumber: number
  colorStrength: number // 0 ~ 1
}

const CellReserch: React.FC<CellReserchProps> = ({ reserchNumber, colorStrength, children }) => {
  const classes = useStyles()

  // 森のグラデーション
  const maxBrightness = 100
  const minBrightness = 75
  const brightness = minBrightness + (maxBrightness - minBrightness) * (1 - colorStrength)
  const backgroundColor = `hsl(120, 40%, ${brightness}%)`

  return (
    <CellReserchDefault style={{ backgroundColor }}>
      <Badge
        badgeContent={reserchNumber}
        max={99}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        className={classes.badge}
        style={{ width: '100%', height: '100%' }}
      >
        <div>{children}</div>
      </Badge>
    </CellReserchDefault>
  )
}

interface CellInnerProps {
  onClick?: () => void
  selectedHeatMapCell: MapVector | undefined
  lastSelectedHeatMapCell: MapVector | undefined
  mapVector: MapVector
}

const CellInner: React.FC<CellInnerProps> = ({
  children,
  onClick,
  selectedHeatMapCell,
  lastSelectedHeatMapCell,
  mapVector
}) => {
  const classes = useStyles()

  const lastOverlay = lastSelectedHeatMapCell ? !_.isEqual(lastSelectedHeatMapCell, mapVector) : false
  const overlay = selectedHeatMapCell ? !_.isEqual(selectedHeatMapCell, mapVector) : false

  const cssProps = useSpring({
    opacity: overlay ? 0.5 : 1,
    from: { opacity: lastOverlay ? 0.5 : 1 }
  })

  return (
    <animated.div
      style={{
        width: '100%',
        ...cssProps
      }}
    >
      <ButtonBase
        style={{
          width: '100%',
          textAlign: 'left'
        }}
        className={`${classes.mapCell}`}
        focusVisibleClassName={classes.focusVisible}
      >
        <CellInnerDefault style={{ width: '100%' }} onClick={onClick}>
          {children}
        </CellInnerDefault>
      </ButtonBase>
    </animated.div>
  )
}

const getMinMaxReserchNumber = (cellMap: HeatMapCellPapersData[][]) => {
  let maxReserchNumber = 0
  let minReserchNumber = 100000

  // cellMapの最大と最小の論文数を取得する
  cellMap.forEach(x => {
    x.forEach(cell => {
      if (maxReserchNumber < cell.papers.length) {
        maxReserchNumber = cell.papers.length
      }
      if (minReserchNumber > cell.papers.length) {
        minReserchNumber = cell.papers.length
      }
    })
  })

  return { minReserchNumber, maxReserchNumber }
}

const getColorStrength = (reserchNumber: number, minReserchNumber: number, maxReserchNumber: number) => {
  // 最大が0→検索件数0なのでなので色は0に
  if (maxReserchNumber === 0) {
    return 0
  }

  return (1.0 * (reserchNumber - minReserchNumber)) / (maxReserchNumber - minReserchNumber)
}

const getFontSize = (name: string) => {
  if (name.length < 20) {
    return { fontSize: '1.0em' }
  }
  if (name.length < 30) {
    return { fontSize: '0.9em' }
  }
  if (name.length < 45) {
    return { fontSize: '0.9em' }
  }
  return { fontSize: '0.8em' }
  // return { fontSize: '0.6rem', wordBreak: 'break-all' as 'break-all' }
}

const Component: React.FC = () => {
  const {
    verticalAxis,
    horizontalAxis,
    isResetted,
    filterTagsV2,
    excludeTagsV2,
    freewords,
    papers,
    filterdPapers,
    cellMap,
    handleIsSearched,
    handleVerticalAxis,
    handleHorizontalAxis,
    handleSelectHeatMapCellTags,
    handleReset,
    hideUnspecifiedTags,
    handleHideUnspecifiedTags
  } = useCprmSearch()

  const verticalTagAxis = tagAxesObject[verticalAxis]
  const horizontalTagAxis = tagAxesObject[horizontalAxis]
  const verticalAxisTags = verticalTagAxis.tags
  const horizontalAxisTags = horizontalTagAxis.tags

  const [selectedHeatMapCell, setSelectedHeatMapCell] = React.useState<{
    x: number
    y: number
  }>()
  const [lastSelectedHeatMapCell, setLastSelectedHeatMapCell] = React.useState<{
    x: number
    y: number
  }>()

  // verticalAxis, horizontalAxisのどれかが変化したら一度リセットとする
  React.useEffect(() => {
    setSelectedHeatMapCell(undefined)
    setLastSelectedHeatMapCell(undefined)
  }, [verticalAxisTags, horizontalAxisTags])

  // isResettedフラグがtrueになってもリセットとする
  React.useEffect(() => {
    if (isResetted) {
      setSelectedHeatMapCell(undefined)
      setLastSelectedHeatMapCell(undefined)
    }
  }, [isResetted])

  const { minReserchNumber, maxReserchNumber } = getMinMaxReserchNumber(cellMap)

  const AxisXTagNameCell = React.useMemo(
    // @ts-ignore // FIXME: エラー対処
    () => styled(CellDefault)`
      width: ${`calc(100% / ${horizontalAxisTags.length})`};
    `,
    [horizontalAxisTags.length]
  )

  const AxisYTagNameCell = React.useMemo(
    () => styled(CellDefault)`
      width: ${`calc(100% / ${horizontalAxisTags.length})`};
    `,
    [horizontalAxisTags.length]
  )

  const nameCellStyle: React.CSSProperties = {
    lineHeight: '1.1',
    wordBreak: 'break-word',
    padding: '4px'
  }

  const selectHeatMapCell = React.useCallback(
    (args: { x: number; y: number } | undefined) => {
      if (args) {
        handleSelectHeatMapCellTags([verticalAxisTags[args.y].id, horizontalAxisTags[args.x].id])
        console.log('handleIsSearched')
        handleIsSearched(true)
      } else {
        handleSelectHeatMapCellTags([])
      }
    },
    [handleIsSearched, handleSelectHeatMapCellTags, horizontalAxisTags, verticalAxisTags]
  )

  // NOTE: cellMap（配列）に依存しているのでuseMemoの不具合に注意
  const heatMapRows = React.useMemo(
    () => (
      <Box
        style={{
          display: 'flex',
          flexWrap: 'nowrap'
        }}
      >
        <Box
          style={{
            width: '0px',
            marginTop: `calc(${Layouts.HeatMap.CellHeight} * ${(verticalTagAxis.tags.length + 2) / 2})`
          }}
        >
          <Typography
            variant="subtitle2"
            component="div"
            style={{
              width: `calc(${verticalTagAxis.name.length} * 0.7em)`,
              minWidth: '100px',
              maxWidth: '300px',
              textAlign: 'center',
              lineHeight: '1.5em',
              transform: 'rotate(270deg)',
              transformOrigin: '0% 100%',
              whiteSpace: 'nowrap',
              backgroundColor: Colors.Tags[verticalTagAxis.id],
              padding: '0 10px',
              marginBottom: '10px',
              borderRadius: '1rem'
            }}
          >
            {verticalTagAxis.name}
          </Typography>
        </Box>
        <Box
          style={{
            width: '100%'
          }}
        >
          {verticalAxisTags.map((yTag, yIndex) => (
            <Row key={`verticalAxisTags-${yTag.id}`}>
              <AxisYTagNameCell
                style={{
                  borderRight: `solid 1px ${Colors.HeatMap.Border}`
                }}
              >
                <CellText
                  style={{
                    ...nameCellStyle,
                    ...getFontSize(yTag.name)
                  }}
                >
                  {yTag.name}
                </CellText>
              </AxisYTagNameCell>
              {horizontalAxisTags.map((xTag, xIndex) => {
                const heatmapCellPapersData = cellMap[xIndex][yIndex]
                const reserchNumber = heatmapCellPapersData.papers.length
                const sampledPaper = heatmapCellPapersData.papers.slice(0, 3)
                return (
                  <AxisXTagNameCell key={`Cell-${yTag.id}-${xTag.id}`}>
                    <CellInner
                      selectedHeatMapCell={selectedHeatMapCell}
                      lastSelectedHeatMapCell={lastSelectedHeatMapCell}
                      mapVector={{ x: xIndex, y: yIndex }}
                      onClick={() => {
                        if (
                          !_.isEqual(selectedHeatMapCell, {
                            x: xIndex,
                            y: yIndex
                          })
                        ) {
                          setLastSelectedHeatMapCell(selectedHeatMapCell)
                          setSelectedHeatMapCell({ x: xIndex, y: yIndex })
                          selectHeatMapCell({ x: xIndex, y: yIndex })
                        } else {
                          setLastSelectedHeatMapCell(selectedHeatMapCell)
                          setSelectedHeatMapCell(undefined)
                          selectHeatMapCell(undefined)
                        }
                      }}
                    >
                      <CellReserch
                        reserchNumber={reserchNumber}
                        colorStrength={getColorStrength(reserchNumber, minReserchNumber, maxReserchNumber)}
                      >
                        {sampledPaper.map(p => (
                          <div key={p.id}>
                            {_.truncate(getFirstAuthor(p.authors), {
                              length: 20
                            })}
                            , {p.year}
                          </div>
                        ))}
                      </CellReserch>
                    </CellInner>
                  </AxisXTagNameCell>
                )
              })}
            </Row>
          ))}
        </Box>
      </Box>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      cellMap,
      horizontalAxisTags,
      lastSelectedHeatMapCell,
      maxReserchNumber,
      minReserchNumber,
      nameCellStyle,
      selectHeatMapCell,
      selectedHeatMapCell,
      verticalAxisTags,
      verticalTagAxis.id,
      verticalTagAxis.name,
      verticalTagAxis.tags.length
    ]
  )

  const styleBorder: React.CSSProperties = {
    // borderLeft: '1px solid'
  }
  const styleBorderLeft: React.CSSProperties = {
    paddingRight: '12px'
  }
  const styleBorderRight: React.CSSProperties = {
    paddingLeft: '12px'
  }

  const tagAxisTd: React.CSSProperties = {
    textAlign: 'center'
  }

  const tagAxisButton: React.CSSProperties = {
    borderRadius: '16px'
  }

  // eslint-disable-next-line deprecation/deprecation
  const AxisSelectTypography = styled(Typography).attrs(_props => ({
    variant: 'body2'
  }))`
    padding: 2px;
  `

  return (
    <>
      <Typography variant="h2">Heat Map</Typography>
      <Typography variant="body2" gutterBottom>
        {/* eslint-disable-next-line react/jsx-curly-brace-presence */}
        {
          'The research map is a two-dimensional matrix whose axes correspond to tag categories. Papers are positioned on the matrix according to their tag(s) for each category. Select different categories to change the axes of the matrix.'
        }
      </Typography>

      <XScrollableBox paddingY={2}>
        <table style={{ borderCollapse: 'collapse', minWidth: '940px' }}>
          <thead>
            <tr>
              <th> </th>
              <th colSpan={2} style={styleBorder}>
                <Typography variant="subtitle2">
                  Neuroscience
                  <HelpPopover>
                    <NeuroscienceHelpText />
                  </HelpPopover>
                </Typography>
              </th>
              <th colSpan={2} style={styleBorder}>
                <Typography variant="subtitle2">
                  Psychiatry
                  <HelpPopover>
                    <PsychiatryHelpText />
                  </HelpPopover>
                </Typography>
              </th>
              <th colSpan={3} style={styleBorder}>
                <Typography variant="subtitle2">
                  Computation
                  <HelpPopover>
                    <ComputationHelpText />
                  </HelpPopover>
                </Typography>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              style={
                {
                  /* borderTop: '1px solid' */
                }
              }
            >
              <td style={{ ...styleBorderLeft }}>
                <Typography variant="subtitle2">Vertical axis</Typography>
              </td>
              {tagAxes.map((tagAxis, index) => (
                <td
                  key={tagAxis.id}
                  style={{
                    ...tagAxisTd,
                    ...([0, 2, 4].includes(index) ? styleBorder : {}),
                    ...([1, 3].includes(index) ? styleBorderLeft : {}),
                    ...([0, 2, 4].includes(index) ? styleBorderRight : {})
                  }}
                >
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => handleVerticalAxis(tagAxis.id)}
                    style={{
                      ...tagAxisButton,
                      ...(tagAxis.id === verticalAxis ? { backgroundColor: Colors.Tags[tagAxis.id] } : {})
                    }}
                  >
                    <AxisSelectTypography>{tagAxis.name}</AxisSelectTypography>
                  </Button>
                </td>
              ))}
            </tr>
            <tr
              style={
                {
                  /* borderTop: '1px solid' */
                }
              }
            >
              <td style={{ ...styleBorderLeft }}>
                <Typography variant="subtitle2">Horizontal axis</Typography>
              </td>
              {tagAxes.map((tagAxis, index) => (
                <td
                  key={tagAxis.id}
                  style={{
                    ...tagAxisTd,
                    ...([0, 2, 4].includes(index) ? styleBorder : {}),
                    ...([1, 3].includes(index) ? styleBorderLeft : {}),
                    ...([0, 2, 4].includes(index) ? styleBorderRight : {})
                  }}
                >
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => handleHorizontalAxis(tagAxis.id)}
                    style={{
                      ...tagAxisButton,
                      ...(tagAxis.id === horizontalAxis ? { backgroundColor: Colors.Tags[tagAxis.id] } : {})
                    }}
                  >
                    <AxisSelectTypography>{tagAxis.name}</AxisSelectTypography>
                  </Button>
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </XScrollableBox>

      <XScrollableBox>
        <Map>
          <Row
            style={{
              minWidth: `calc(${Layouts.HeatMap.CellMinWidth} * (${horizontalTagAxis.tags.length} + 1)`
            }}
          >
            <Typography
              variant="subtitle2"
              style={{
                minWidth: '100px',
                textAlign: 'center',
                margin: '0 auto',
                lineHeight: '1.5em',
                backgroundColor: Colors.Tags[horizontalTagAxis.id],
                padding: '0 10px',
                borderRadius: '1rem'
              }}
            >
              {horizontalTagAxis.name}
            </Typography>
          </Row>
          <Row>
            <AxisXTagNameCell
              style={{
                borderBottom: `solid 1px ${Colors.HeatMap.Border}`,
                borderRight: `solid 1px ${Colors.HeatMap.Border}`
              }}
            />
            {horizontalAxisTags.map(xTag => (
              <AxisXTagNameCell
                key={`horizontalAxisTags-${xTag.id}`}
                style={{
                  borderBottom: `solid 1px ${Colors.HeatMap.Border}`
                }}
              >
                <CellText style={{ ...nameCellStyle, ...getFontSize(xTag.name) }}>{xTag.name}</CellText>
              </AxisXTagNameCell>
            ))}
          </Row>
          {heatMapRows}
        </Map>
      </XScrollableBox>

      <Box
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginTop: '30px',
          height: '20px'
        }}
      >
        <Typography
          variant="subtitle2"
          component="div"
          color="textSecondary"
          style={{ marginLeft: 'auto', marginRight: '10px' }}
        >
          LESS
        </Typography>
        <div
          style={{
            width: '50%',
            background: `linear-gradient(to right, ${'hsl(120, 40%, 100%)'}, ${'hsl(120, 40%, 75%)'})`
          }}
        >
          <Box style={{ textAlign: 'center' }}>
            <Typography variant="body2">Number of Papers</Typography>
          </Box>
        </div>
        <Typography
          variant="subtitle2"
          component="div"
          color="textSecondary"
          style={{ marginRight: 'auto', marginLeft: '10px' }}
        >
          MORE
        </Typography>
      </Box>

      <Box style={{ margin: '10px 0' }}>
        <FormControlLabel
          control={
            <Checkbox
              color="primary"
              size="small"
              checked={hideUnspecifiedTags}
              onChange={e => handleHideUnspecifiedTags(e.target.checked)}
              value="hideUnspecifiedTags"
            />
          }
          label="Hide unspecified tags (only current vertical/horizontal axes)"
        />
      </Box>

      <Box paddingTop={1}>
        <SearchDisplayFilteringInfo
          papers={papers}
          filteringPapers={filterdPapers}
          freewords={freewords}
          filterTagsV2={filterTagsV2}
          excludeTagsV2={excludeTagsV2}
          handleReset={handleReset}
        />
      </Box>
    </>
  )
}

// const WrapedComponent = () => (
//   <HeatMapProvider>
//     <Component />
//   </HeatMapProvider>
// )

export default Component
